import {
  CSSProperties,
  ReactNode,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { GetComplianceProgramProgressQuery } from "types/graphql-schema";

import Loading from "@components/loading/loading";
import { ManagedTableColumn } from "@components/table/managed-table";
import Table, {
  TableBody,
  TableBodyCell,
  TableBodyRow,
  TableContainer,
  TableHeadCell,
  TableHeadRow,
} from "@components/table/table";
import { TableSortDir, TableSticky } from "@components/table/table-constants";
import { classNames } from "@helpers/css";

import { useProgramsSection } from "../programs-section-context";
import {
  complianceProgramReportingTableColNameClassName,
  complianceProgramReportingTableColsClassName,
  getColumnDueDate,
} from "./compliance-program-progress-constants";
import {
  ComplianceProgramReportingEntityTableCol,
  ComplianceProgramReportingEntityTableParticipantStatusWithNominations,
  ComplianceProgramReportingTableEntity,
  useComplianceProgramProgressEntityColumns,
} from "./compliance-program-progress-entity-table";
import { useComplianceProgramProgressUserColumns } from "./compliance-program-progress-user-table";
import ProgramProgressColumnHeader from "./program-progress-column-header";

export type ComplianceProgramProgressTableSection =
  | {
      type: "entities";
      key: string;
      title?: string;
      entities: ComplianceProgramReportingTableEntity[];
      // Left out by a section that always has a row.
      emptyText?: string;
    }
  | {
      type: "users";
      key: string;
      title: string;
      participantStatuses: ComplianceProgramReportingEntityTableParticipantStatusWithNominations[];
      emptyText: string;
      isLoading?: boolean;
    };

// A section title's top line, drawn on its cells so it moves with them when they stick. The
// table's own line between rows belongs to neither row and stays where it was.
const sectionTitleTopLineClassName =
  "before:absolute before:inset-x-0 before:top-0 before:h-px before:bg-gray-200 before:content-['']";

const sortRows = <T,>(
  rows: T[],
  columns: ManagedTableColumn<T>[],
  sortKey: string,
  sortDir: TableSortDir,
) => {
  const getValue = columns.find((col) => col.sortKey === sortKey)?.value;
  if (!getValue) {
    return rows;
  }
  const sorted = [...rows].sort((a, b) => {
    const aValue = getValue(a);
    const bValue = getValue(b);
    return typeof aValue === "number" && typeof bValue === "number"
      ? aValue - bValue
      : `${aValue}`.localeCompare(`${bValue}`);
  });
  return sortDir === "desc" ? sorted.reverse() : sorted;
};

/**
 * The whole Progress tab in one table: the organization (or the team or reporting group
 * being looked at), then each section - teams, the viewer's reporting groups, people - as
 * a titled run of rows under the one header.
 *
 * One table rather than one per section, so every column is the same width all the way
 * down and the step names stay pinned above whichever section is in view. The table scrolls
 * inside a box no taller than the screen, because a header can only stick to the box that
 * scrolls it: the page cannot do it here, as the steps also scroll sideways inside the box.
 *
 * Sorting by a column sorts each section within itself; a team is never sorted in among
 * the people.
 */
const ComplianceProgramProgressTable = ({
  complianceProgram,
  cols,
  participantStatuses,
  sections,
}: {
  complianceProgram: NonNullable<
    GetComplianceProgramProgressQuery["complianceProgram"]
  >;
  cols: ComplianceProgramReportingEntityTableCol[];
  // Everyone in view, for the due date under each step name.
  participantStatuses: ComplianceProgramReportingEntityTableParticipantStatusWithNominations[];
  sections: ComplianceProgramProgressTableSection[];
}) => {
  const { noun } = useProgramsSection();
  const [sortKey, setSortKey] = useState("name");
  const [sortDir, setSortDir] = useState<TableSortDir>("asc");
  // A section's title sticks just under the step names while its rows are in view, so it
  // needs the header's height: one line or two, depending on whether a step has a due date.
  // Rounded down: the header is often half a pixel taller, and the half pixel of line under
  // it is the table's to paint, not the header's, so it travels away with the rows. The
  // title's own top line covers that pixel; the header paints over any overlap.
  const containerRef = useRef<HTMLDivElement>(null);
  const [sectionTitleTop, setSectionTitleTop] = useState(0);
  useLayoutEffect(() => {
    const head = containerRef.current?.querySelector("thead");
    if (!head) {
      return;
    }
    const observer = new ResizeObserver(() =>
      setSectionTitleTop(Math.floor(head.getBoundingClientRect().height)),
    );
    observer.observe(head);
    return () => observer.disconnect();
  }, []);
  const entityColumns = useComplianceProgramProgressEntityColumns({
    complianceProgram,
    cols,
  });
  const userColumns = useComplianceProgramProgressUserColumns({
    complianceProgram,
    cols,
  });
  // Name, one per step, and a last empty column that takes the spare width on a wide screen.
  const columnCount = cols.length + 2;

  const handleSort = (key: string) => {
    if (key === sortKey) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
    }
  };

  const renderRows = <T,>(
    rows: T[],
    columns: ManagedTableColumn<T>[],
    getRowKey: (row: T) => string | number,
  ) =>
    sortRows(rows, columns, sortKey, sortDir).map((row) => (
      <TableBodyRow key={getRowKey(row)}>
        {columns.map((col) => (
          <TableBodyCell key={col.header} className={col.cellClassName}>
            {col.render?.(row)}
          </TableBodyCell>
        ))}
        <TableBodyCell />
      </TableBodyRow>
    ));

  // One cell across the whole row, for a section's empty or loading state. A cell as wide
  // as the table has nowhere to stick, so its content sticks instead: it stays at the left
  // edge, under the names, when the steps are scrolled sideways. The edge shadow that marks
  // a pinned name column off from the steps would sit alone at the far right.
  const renderFullWidthRow = (children: ReactNode) => (
    <TableBodyRow>
      <TableBodyCell colSpan={columnCount} style={{ boxShadow: "none" }}>
        <div className="sticky left-5 w-fit">{children}</div>
      </TableBodyCell>
    </TableBodyRow>
  );

  return (
    // The table shrinks to the height its page has left, so it is the one thing that
    // scrolls: were the page to scroll as well, its last rows would sit below the fold
    // until the page caught up. Each page that shows it lays it out in a flex column for this.
    <TableContainer
      ref={containerRef}
      scroll
      className="overscroll-x-contain min-h-0"
      style={{ "--section-title-top": `${sectionTitleTop}px` } as CSSProperties}
      aria-label={`${noun} progress table`}
    >
      <Table sticky={TableSticky.topAndLeft}>
        <TableHeadRow>
          <TableHeadCell
            className={complianceProgramReportingTableColNameClassName}
            sorted={sortKey === "name" ? sortDir : undefined}
            onClick={() => handleSort("name")}
          >
            Name
          </TableHeadCell>
          {cols.map((col) => (
            <TableHeadCell
              key={col.keyName}
              className={complianceProgramReportingTableColsClassName}
              sorted={sortKey === col.keyName ? sortDir : undefined}
              onClick={() => handleSort(col.keyName)}
            >
              <ProgramProgressColumnHeader
                label={col.label}
                due={getColumnDueDate(participantStatuses, col.keyName)}
              />
            </TableHeadCell>
          ))}
          <TableHeadCell />
        </TableHeadRow>
        {sections.map((section) => (
          <TableBody
            key={section.key}
            // A titled section draws its own top line, below; the table's line between
            // sections would stay behind when the title sticks, and doubled up beside it
            // when it does not.
            className={classNames(section.title && "border-t-0!")}
            aria-label={
              section.type === "users"
                ? `${noun} progress user table`
                : undefined
            }
          >
            {/* The title stays under the step names while its section scrolls past, and
                the next section's title, coming up behind it, takes its place. It sits
                over the rows sliding beneath it, and its name cell over its own wide cell
                when the steps scroll sideways; the step names stay over both. */}
            {section.title && (
              <TableBodyRow className="border-t-0">
                <TableBodyCell
                  className={classNames(
                    complianceProgramReportingTableColNameClassName,
                    "bg-gray-100 py-2 font-medium text-gray-700",
                    "sticky top-[var(--section-title-top)] z-[3]",
                    sectionTitleTopLineClassName,
                  )}
                >
                  {section.title}
                </TableBodyCell>
                <TableBodyCell
                  colSpan={columnCount - 1}
                  className={classNames(
                    "bg-gray-100 py-2 sticky top-[var(--section-title-top)] z-[2]",
                    sectionTitleTopLineClassName,
                  )}
                />
              </TableBodyRow>
            )}
            {section.type === "users" && section.isLoading
              ? renderFullWidthRow(<Loading mini size={5} />)
              : section.type === "users"
                ? section.participantStatuses.length === 0
                  ? renderFullWidthRow(section.emptyText)
                  : renderRows(
                      section.participantStatuses,
                      userColumns,
                      (participantStatus) => participantStatus.user.id,
                    )
                : section.entities.length === 0
                  ? renderFullWidthRow(section.emptyText)
                  : renderRows(
                      section.entities,
                      entityColumns,
                      (entity) => `${entity.type}-${entity.id}`,
                    )}
          </TableBody>
        ))}
      </Table>
    </TableContainer>
  );
};

export default ComplianceProgramProgressTable;
